import { useId } from "preact/hooks";
import { useVariantPossibilities } from "$store/sdk/useVariantPossiblities.ts";
import { useSignal } from "@preact/signals";
import Image from "apps/website/components/Image.tsx";
import Icon from "$store/components/ui/Icon.tsx";
import {
  selectedProducts,
  selectProduct,
  unselectProduct,
} from "$store/components/product/content/BuyTogether/selectedProducts.ts";
import type { Product } from "apps/commerce/types.ts";

interface Props {
  product: Product;
}

export default function ProductCard({ product }: Props) {
  const id = useId();
  const { image, name, isVariantOf, url } = product;

  const variants = useVariantPossibilities({
    productUrl: url,
    isVariantOf,
  });

  const hasVariants = !!Object.keys(variants).length;
  const selectedVariant = useSignal<Product | null>(null);
  const showError = useSignal(false);
  const isSelected = selectedProducts.value.some((p) =>
    p.sku === selectedVariant.value?.sku || p.sku === product.sku
  );

  return (
    <div class="h-full grid content-between gap-6">
      <div class="grid gap-3.5">
        {image?.[0].url
          ? (
            <Image
              class="w-full"
              src={image[0].url}
              alt={name}
              width={200}
              height={200}
              loading="lazy"
              decoding="async"
              fetchPriority="low"
            />
          )
          : (
            <div class="w-full aspect-square rounded-2xl bg-tw-base-100 bg-opacity-50" />
          )}

        <a
          class="h-16 text-tw-neutral-500 leading-5 font-nunito font-bold line-clamp-3"
          href={url}
        >
          {isVariantOf?.name} {selectedVariant.value?.name ?? name}
        </a>

        {hasVariants && (
          <div class="flex flex-wrap gap-2.5">
            {Object.entries(variants).map(([name, values]) => (
              <details id={`${id}-${name}`} class="flex-1 relative">
                <summary class="h-12 px-3 border border-tw-primary-100 rounded-full text-tw-neutral-400 flex justify-between items-center cursor-pointer">
                  {selectedVariant.value?.name ?? name}
                  <Icon
                    id="ChevronRight"
                    class="text-tw-primary-100 rotate-90 transition-all duration-300 group-open:-rotate-90"
                    size={20}
                    strokeWidth={3}
                  />
                </summary>

                <ul class="w-full max-h-40 py-3 mb-1 border border-tw-primary-100 rounded-2xl overflow-y-auto bg-white absolute bottom-full z-10">
                  {Object.entries(values).map(([value, [link]]) => (
                    <li>
                      <button
                        class="w-full py-3 px-1 text-tw-neutral-400 text-sm text-center font-medium cursor-pointer transition-colors duration-300 hover:bg-tw-base-100 hover:bg-opacity-50 disabled:text-tw-neutral-300"
                        disabled={!link.available}
                        onClick={() => {
                          const variant = isVariantOf?.hasVariant.find((
                            variant,
                          ) => variant.sku === String(link.id)) as Product;

                          selectedVariant.value = variant;

                          selectProduct(variant);

                          const details = document.querySelector<
                            HTMLDetailsElement
                          >(`#${id}-${name}`);

                          if (details) {
                            details.open = false;
                          }
                        }}
                      >
                        {value}
                      </button>
                    </li>
                  ))}
                </ul>
              </details>
            ))}
          </div>
        )}
      </div>

      <div class="flex items-center gap-1.5 relative">
        {showError.value && (
          <span class="pb-2 text-tw-danger-200 text-sm font-nunito font-medium text-center block absolute bottom-full left-0 right-0">
            Selecione uma opção
          </span>
        )}

        <div class="relative leading-[0]">
          <input
            id={id}
            class="appearance-none w-5 h-5 rounded bg-tw-base-100 cursor-pointer checked:bg-tw-primary-100 peer"
            name={name}
            type="checkbox"
            checked={isSelected}
            onChange={(event) => {
              if (!hasVariants) {
                if (isSelected) {
                  unselectProduct(product);
                } else {
                  selectProduct(product);
                }

                return;
              }

              if (!selectedVariant.value) {
                event.currentTarget.checked = false;

                showError.value = true;

                setTimeout(() => {
                  showError.value = false;
                }, 2000);
              } else if (!isSelected) {
                selectProduct(selectedVariant.value);
              } else {
                unselectProduct(selectedVariant.value);
              }
            }}
          />

          <Icon
            id="Check"
            class="opacity-0 text-white absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none peer-checked:opacity-100"
            width={12}
            height={9}
          />
        </div>

        <label
          class="text-tw-secondary-500 text-sm font-bold cursor-pointer"
          for={id}
        >
          Quero comprar este junto
        </label>
      </div>
    </div>
  );
}
